import { useEffect, useReducer, useRef } from "react";
import { readStream } from "@/shared/api/stream";
import { runStreamPath } from "@/shared/api/runs";
import { finishStep, imagesOf, startStep, stopStep, wasCutOff } from "@/shared/api/threads";
import type { ContextAgent, RunEnd, RunEvent, RunEventMessage, RunStep, TurnPart } from "@/shared/types";

/* Watches one run and folds its events into what a chat shows while it happens:
   the turn as it is written — each model step's reasoning, words and tool calls,
   in the order the transcript will save them — and how it ended or that it
   paused. The transcript is still the truth: when the run ends or pauses the
   caller refetches the thread — and what a pause asks is read off the run
   (`ApiRun.pending`), not off this stream.

   A pause is not an end. The server closes the stream at the pause the run
   still waits on (one already answered it reads past), and once the person
   answers, the same run goes on: its stream is read again from the pause, so
   the turn keeps what it wrote before it. */

export interface RunView {
  /** The turn so far, across every model step and pause. */
  parts: TurnPart[];
  /** Where the model step in flight starts in `parts`: its reasoning, then its words, go there. */
  step: number;
  /** The model is working with nothing on screen to show it — no words, reasoning or call
      coming in, no tool running — so the turn shows its status line instead. */
  quiet: boolean;
  /** What the run was asked (`run.start`) — for a turn nobody sent from this screen. */
  asked: string | null;
  /** The agent that message to Builder is about (`run.start`'s `context_agent`). */
  askedAbout: ContextAgent | null;
  /** `pending`: the run paused on a person. */
  status: "idle" | "live" | "pending" | "ended" | "failed";
  /** `run.end`'s body once it arrives. */
  end: RunEnd | null;
  /** Why the stream itself could not be read (a 4xx answer). */
  failure: string | null;
}

const IDLE: RunView = { parts: [], step: 0, quiet: false, asked: null, askedAbout: null, status: "idle", end: null, failure: null };
const LIVE: RunView = { ...IDLE, status: "live", quiet: true };

type Action = { type: "event"; event: RunEvent } | { type: "reset"; live: boolean } | { type: "failed"; message: string };

const wordsOf = (message: RunEventMessage) =>
  message.content.filter((part) => part.type === "text").map((part) => String(part.data)).join("");

/** The step's reasoning or words, grown by `more` — or set to `all`, the words the finished
    message says it wrote. One part of each per step: its reasoning first, then its words,
    then the calls it asks for. */
function write(parts: TurnPart[], step: number, kind: "thinking" | "text", more: string, all?: string): TurnPart[] {
  const at = parts.findIndex((p, i) => i >= step && p.kind === kind);
  if (at < 0) {
    const place = kind === "text" && parts[step]?.kind === "thinking" ? step + 1 : step;
    return [...parts.slice(0, place), { kind, text: all ?? more }, ...parts.slice(place)];
  }
  const part = parts[at] as Extract<TurnPart, { kind: "thinking" | "text" }>;
  const text = all ?? part.text + more;
  return text === part.text ? parts : parts.map((p, i) => (i === at ? { ...part, text } : p));
}

/** The call `id` changed by `change`; a call not seen yet joins the end of the turn. */
function call(parts: TurnPart[], id: string, name: string, change: (step: RunStep) => RunStep): TurnPart[] {
  const at = parts.findIndex((p) => p.kind === "tool" && p.step.id === id);
  if (at < 0) return [...parts, { kind: "tool", step: change(startStep({ id, name, args: {} })) }];
  return parts.map((p, i) => (i === at && p.kind === "tool" ? { ...p, step: change(p.step) } : p));
}

/** The step's images, after its reasoning and words and before its calls: where the saved
    turn draws them. Set, not added, so a message seen twice draws them once, and the
    places held for images still being made go when the message brings them. */
function placeImages(parts: TurnPart[], step: number, images: TurnPart[]): TurnPart[] {
  const kept = parts.filter((p, i) => i < step || p.kind !== "image");
  const at = kept.reduce((end, p, i) => (i >= step && (p.kind === "thinking" || p.kind === "text") ? i + 1 : end), step);
  return [...kept.slice(0, at), ...images, ...kept.slice(at)];
}

const running = (parts: TurnPart[]) => parts.some((p) => p.kind === "tool" && p.step.status === "running");

/** Without the calls the model wrote that never started — one a person was asked about, one
    a stop cut off. The transcript says what became of them; the live turn won't guess. */
const started = (parts: TurnPart[]) => parts.filter((p) => p.kind !== "tool" || !p.step.drafting);

export function reduceRunView(view: RunView, action: Action): RunView {
  if (action.type === "reset") return action.live ? LIVE : IDLE;
  if (action.type === "failed") return { ...view, status: "failed", quiet: false, failure: action.message };
  const { event } = action;
  switch (event.name) {
    case "run.start": // a run going on after its answer asks nothing new; its turn carries on below
      return event.data.continued
        ? { ...view, status: "live", quiet: true, step: view.parts.length }
        : { ...view, status: "live", quiet: true, asked: event.data.input.text, askedAbout: event.data.context_agent ?? null };
    case "run.retry": // the attempt that died: everything it wrote goes
      return { ...view, parts: [], step: 0, quiet: true };
    case "model.pre": {
      const parts = started(view.parts);
      return { ...view, parts, step: parts.length, quiet: true };
    }
    case "model.thinking":
      return { ...view, quiet: false, parts: write(view.parts, view.step, "thinking", event.data.text) };
    case "model.delta":
      return { ...view, quiet: false, parts: write(view.parts, view.step, "text", event.data.text) };
    case "model.image": {
      const { id, model } = event.data;
      if (view.parts.some((p) => p.kind === "image" && p.from === "making" && p.id === id)) return view; // seen again after a reconnect
      const images = view.parts.filter((p, i) => i >= view.step && p.kind === "image");
      const making: TurnPart = { kind: "image", from: "making", id, model, since: event.ts * 1000 };
      return { ...view, quiet: false, parts: placeImages(view.parts, view.step, [...images, making]) };
    }
    case "model.post": {
      // The finished message is the truth for the step's words and images: a delta the stream
      // dropped is back, and an image that failed takes its held place with it.
      const { message } = event.data;
      const said = wordsOf(message);
      const written = said ? write(view.parts, view.step, "text", "", said) : view.parts;
      const marked = wasCutOff(message) ? written.map((p, i) => (i >= view.step && p.kind === "text" ? { ...p, cut: true } : p)) : written;
      return { ...view, quiet: false, parts: placeImages(marked, view.step, imagesOf(message.content)) };
    }
    case "tool.args": {
      const { call_id, name, args_delta } = event.data;
      if (!call_id) return view;
      return { ...view, quiet: false, parts: call(view.parts, call_id, name, (s) => ({ ...s, drafting: true, argsText: (s.argsText ?? "") + args_delta })) };
    }
    case "tool.pre": {
      const { call: asked } = event.data;
      const parts = call(view.parts, asked.id, asked.name, (s) => ({ ...startStep(asked), argsText: s.argsText }));
      return { ...view, quiet: false, parts };
    }
    case "tool.post": {
      const { message } = event.data;
      // Done, its arguments are its `args`: the text written on the way goes, as in the transcript.
      const finish = ({ argsText, ...step }: RunStep) => finishStep(step, wordsOf(message), message.meta);
      const parts = view.parts.map((p) => (p.kind === "tool" && p.step.id === message.tool_call_id ? { ...p, step: finish(p.step) } : p));
      return { ...view, parts, quiet: !running(parts) };
    }
    case "run.pending":
      return { ...view, status: "pending", quiet: false };
    case "run.end":
      return {
        ...view,
        status: "ended",
        quiet: false,
        parts: started(view.parts)
          .filter((p) => p.kind !== "image" || p.from !== "making") // an image still being made never came
          .map((p) => (p.kind === "tool" ? { ...p, step: stopStep(p.step) } : p)),
        end: event.data,
      };
    default:
      return view;
  }
}

/** One run's stream being read: where it got to, so a pause is read on from there. */
interface Reading {
  key: string;
  runId: string;
  cursor: string;
  /** A request is open, or about to reconnect. */
  active: boolean;
  /** `run.end` arrived: nothing more will. */
  ended: boolean;
  abort: AbortController;
}

/**
 * Stream `runId` from its first event and reduce it. A new `attempt` of the run
 * starts over from the first event.
 *
 * The reading stops at `run.end`, or where the server closes the stream right
 * after a `run.pending`: the pause the run waits on. `waiting` is the run's own
 * status: when it stops being `pending` — someone answered — the stream is read
 * again from the pause.
 *
 * `onSettle` fires when the run ends or pauses, so the caller can read the
 * transcript and the run's status again.
 */
export default function useRunStream(
  runId: string | null,
  attempt: number,
  waiting: boolean,
  onSettle?: () => void,
): RunView {
  const [view, dispatch] = useReducer(reduceRunView, IDLE);
  const onSettleRef = useRef(onSettle);
  onSettleRef.current = onSettle;
  const reading = useRef<Reading | null>(null);

  useEffect(() => {
    const read = (r: Reading) => {
      r.active = true;
      let last: RunEvent["name"] | null = null;
      readStream<RunEvent>(runStreamPath(r.runId), {
        since: r.cursor,
        signal: r.abort.signal,
        onEvent: (event) => {
          r.cursor = event.id;
          last = event.name;
          dispatch({ type: "event", event });
          if (event.name !== "run.end") return false;
          r.ended = true;
          onSettleRef.current?.();
          return true;
        },
        // Closed right after a pause: the one the run waits on. Closed anywhere else, it dropped.
        onClose: () => {
          if (last !== "run.pending") return false;
          onSettleRef.current?.();
          return true;
        },
      })
        .catch((err: Error) => {
          console.error(err);
          dispatch({ type: "failed", message: err.message });
        })
        .finally(() => {
          r.active = false;
        });
    };

    const key = runId ? `${runId}:${attempt}` : "";
    const current = reading.current;
    if (current && current.key === key) {
      if (!waiting && !current.active && !current.ended) read(current); // answered since the reading stopped
      return;
    }
    current?.abort.abort();
    dispatch({ type: "reset", live: !!runId });
    reading.current = runId
      ? { key, runId, cursor: "", active: false, ended: false, abort: new AbortController() }
      : null;
    if (reading.current) read(reading.current);
  }, [runId, attempt, waiting]);

  useEffect(
    () => () => {
      reading.current?.abort.abort();
      reading.current = null;
    },
    [],
  );

  return view;
}
